import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';

import firebase from '../firebase';

/**
 * Compresses an image file to reduce its size
 * @param file - The image file to compress
 * @returns A promise that resolves to a compressed Blob
 */
export const compressImage = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to maintain 1:2 aspect ratio
        const maxWidth = 400; // Max width for the compressed image
        const maxHeight = 800; // Max height for the compressed image
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image with smooth scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with reduced quality
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Could not compress image'));
            }
          },
          'image/jpeg',
          0.6 // 50% quality
        );
      };
      img.onerror = () => reject(new Error('Error loading image'));
    };
    reader.onerror = () => reject(new Error('Error reading file'));
  });
};

export const compressImageThumbnail = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to maintain 1:2 aspect ratio
        const maxWidth = 120; // Max width for the compressed image
        const maxHeight = 240; // Max height for the compressed image
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image with smooth scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with reduced quality
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Could not compress image'));
            }
          },
          'image/jpeg',
          0.7 // 50% quality
        );
      };
      img.onerror = () => reject(new Error('Error loading image'));
    };
    reader.onerror = () => reject(new Error('Error reading file'));
  });
};

/**
 * Uploads an image to Firebase Storage and returns the URLs
 * @param file - The image file to upload
 * @param sessionId - The current session ID
 * @returns A promise that resolves to an object with the download URLs of the uploaded image and its thumbnail
 */
export const uploadImage = async (file: File, sessionId: string): Promise<{ imageUrl: string; imageThumbnailUrl: string }> => {
  if (!file || !sessionId) {
    throw new Error('File and session ID are required');
  }
  
  const storage = getStorage(firebase);
  
  // Compress the main image before upload
  const compressedImage = await compressImage(file);
  // Compress the thumbnail before upload
  const compressedThumbnail = await compressImageThumbnail(file);

  // Create references to the file locations in Firebase Storage
  const timestamp = Date.now();
  const storageRef = ref(storage, `athletes/${sessionId}/${timestamp}_${file.name}`);
  const thumbnailRef = ref(storage, `athletes/${sessionId}/${timestamp}_thumb_${file.name}`);

  // Upload the compressed image
  await uploadBytes(storageRef, compressedImage);
  // Upload the compressed thumbnail
  await uploadBytes(thumbnailRef, compressedThumbnail);

  // Get the download URLs
  const imageUrl = await getDownloadURL(storageRef);
  const imageThumbnailUrl = await getDownloadURL(thumbnailRef);

  return { imageUrl, imageThumbnailUrl };
};

/**
 * Creates an image preview URL for display
 * @param file - The image file to create a preview for
 * @returns A promise that resolves to the preview URL as a string
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsDataURL(file);
  });
}; 